/**
 * TextLinkButton - Pressable text that acts as a link/button
 * Touch target extended via hitSlop (8pt each direction) beyond visual bounds.
 */

import React from 'react';
import { Pressable, Text, type StyleProp, type ViewStyle } from 'react-native';
import { colors, FS_SM } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

const HIT_SLOP = { top: 8, bottom: 8, left: 8, right: 8 };

interface TextLinkButtonProps {
  /** Button label text */
  label: string;
  onPress: () => void;
  /** testID for E2E tests */
  testID: string;
  /** Accessibility label (falls back to label) */
  accessibilityLabel?: string;
  /** 'button' or 'link' — defaults to 'button' */
  accessibilityRole?: 'button' | 'link';
  disabled?: boolean;
  /** Override text color (defaults to theme.primary / colors.primary) */
  color?: string;
  /** Override font size constant (defaults to FS_SM) */
  fontSize?: number;
  /** Extra style on the Pressable container */
  style?: StyleProp<ViewStyle>;
}

export const TextLinkButton = React.memo(function TextLinkButton({
  label,
  onPress,
  testID,
  accessibilityLabel,
  accessibilityRole = 'button',
  disabled = false,
  color,
  fontSize = FS_SM,
  style,
}: TextLinkButtonProps) {
  const { fontScale, fontFamily } = useDisplayTheme();

  const textColor = color ?? colors.primary;

  return (
    <Pressable
      testID={testID}
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityRole={accessibilityRole}
      accessibilityState={{ disabled }}
      onPress={onPress}
      disabled={disabled}
      hitSlop={HIT_SLOP}
      style={style}
    >
      <Text
        allowFontScaling={false}
        style={{
          fontSize: fontSize * fontScale,
          color: textColor,
          fontWeight: '600',
          ...(fontFamily ? { fontFamily } : undefined),
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
});
